import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import { GridCellParams, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import { Box, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CognitoUser from "../../../../../types/CognitoUser";
import createStringOperators from "../../../../dashboard/internals/filters/createStringOperators";
import { FilterRoleInput } from "../../../../dashboard/internals/filters/roleFilter";
import { FilterTypeInput } from "../../../../dashboard/internals/filters/typeFilter";
import { FilterStatusInput } from "../../../../dashboard/internals/filters/statusFilter";

function renderStatus(status: "active" | "inactive") {
  const colors: { [index: string]: "success" | "default" } = {
    active: "success",
    inactive: "default",
  };

  return <Chip label={status} color={colors[status]} size="small" />;
}

export function renderAvatar(
  params: GridCellParams<{ name: string; color: string }, any, any>
) {
  if (params.value == null) {
    return "";
  }

  return (
    <Avatar
      sx={{
        backgroundColor: params.value.color,
        width: "24px",
        height: "24px",
        fontSize: "0.85rem",
      }}
    >
      {params.value.name.toUpperCase().substring(0, 1)}
    </Avatar>
  );
}

export function renderStatusToggleButton(
  params: GridCellParams,
  handleToggleStatus: (user: any) => void
) {
  const { status } = params.row;
  const isActive = status === "active";

  return (
    <Button
      variant="outlined"
      color={isActive ? "error" : "success"}
      size="small"
      onClick={() => handleToggleStatus(params.row)}
      style={{
        minWidth: "100px",
        height: "25px",
        padding: "6px 12px",
        margin: "0",
        textTransform: "none",
        backgroundColor: isActive ? "#f44336" : "#4caf50",
      }}
    >
      {isActive ? "Deactivate" : "Reactivate"}
    </Button>
  );
}

export function getColumns(
  handleEditClick: (arg: any) => void,
  handleToggleStatus: (arg: any) => void,
  handleDeleteClick: (arg: any) => void
): GridColDef[] {
  return [
    {
      field: "Edit Button",
      headerName: "",
      width: 32,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          color="primary"
          sx={{ padding: 0, margin: 0, height: "24px", width: "24px" }}
          onClick={() => handleEditClick(params.row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.value}
          {params.row.isCurrentUser && (
            <Chip label="You" size="small" color="primary" />
          )}
        </Box>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      minWidth: 250,
    },
    {
      field: "role",
      headerName: "Role",
      headerAlign: "right",
      align: "right",
      minWidth: 80,
      filterOperators: createStringOperators(FilterRoleInput),
    },
    {
      field: "type",
      headerName: "Type",
      headerAlign: "right",
      align: "right",
      minWidth: 100,
      filterOperators: createStringOperators(FilterTypeInput),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 80,
      renderCell: (params) => renderStatus(params.value as any),
      filterOperators: createStringOperators(FilterStatusInput),
    },
    {
      field: "Status button",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params) =>
        renderStatusToggleButton(params, handleToggleStatus),
    },
    {
      field: "Delete Button",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 32,
      renderCell: (params) => (
        <IconButton
          color="error"
          sx={{ padding: 0, margin: 0, height: "24px", width: "24px" }}
          onClick={() => handleDeleteClick(params.row)}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];
}

export function formatUsersForTable(users: CognitoUser[]): GridRowsProp {
  return users.map((user) => {
    const attributes = Object.fromEntries(
      user.Attributes.map(({ Name, Value }) => {
        const key = Name.startsWith("custom:")
          ? Name.replace("custom:", "")
          : Name;
        return [key, Value];
      })
    );

    const loggedInEmail = localStorage.getItem("email");

    return {
      id: attributes.email,
      name: `${attributes.given_name || ""} ${
        attributes.family_name || ""
      }`.trim(),
      email: attributes.email || "",
      role: user.Groups[0],
      type: attributes.employeeType || "",
      status: user.Enabled === true ? "active" : "inactive",
      isCurrentUser: attributes.email === loggedInEmail,
    };
  });
}
